import * as React from 'react';
import {
  currentUrl,
  isComponentType,
  isIOS,
  isPage,
  loadPage,
  pushState,
  replaceState,
  translate,
  URLX,
  useDynamicData,
  useObservable,
} from '@avensia/excite';
import { styled } from '@glitz/react';
import { CloseIcon, SearchIcon } from 'webapp/components/icons';
import { isSvenssons, Theme } from 'webapp/theme';
import { createMinLargeViewportDecorator, createMaxSmallViewportDecorator } from 'webapp/viewport';
import { historyEvent, HistoryEventType } from '@avensia/excite/client/history';
import { add as addToSearchHistory } from 'webapp/search/search-history';
import { createTransitionDecorator } from '@avensia/excite-ui';
import { type AllSuggestionsType, getSuggestionId, SEARCH_HISTORY_COUNT, Suggestions } from 'webapp/search/Suggestions';
import { useDesktopMenuState } from 'webapp/shared/use-desktop-menu-state';
import { SEARCH_QUERY_NAME, searchUrl, useSearchSuggestions } from 'webapp/search/utils';
import { Button } from 'webapp/components/button';
import { addSearchEventToDataLayer } from 'webapp/tracking/events/search';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { quickSearchShowObservable } from 'webapp/shared/use-show-quick-search-on-scroll';
import { useOnClickOutside } from '@avensia/excite-ui';

const DEBOUNCE_TIME = 500;
export const SUGGESTIONS_ID = 'suggestions';

type PropType = {
  close?: () => void;
  isLargeViewport?: boolean;
  elementRef?: React.MutableRefObject<HTMLElement | undefined>;
  autoFocus?: boolean;
  suggestionsEnabled?: boolean;
};

interface QuickSearchRef {
  focusInput: () => void;
}

let hasDebounceSearched = false;
let isHardReload = true;

const QuickSearch = React.forwardRef<QuickSearchRef, PropType>(function QuickSearch(props, ref) {
  const showQuickSearch = useObservable(quickSearchShowObservable);
  const previewTimeoutRef = React.useRef<number | undefined>(undefined);
  const shouldPushUrlState = React.useRef<boolean | undefined>(undefined);
  const searchTimeoutRef = React.useRef<number | undefined>(undefined);
  const suggestionsTimeoutRef = React.useRef<number | undefined>(undefined);
  const initalSearchText = isHardReload ? currentUrl().searchParams.get(SEARCH_QUERY_NAME) || '' : '';
  const [searchText, setSearchText] = React.useState(initalSearchText);
  const [searchSuggestionsText, setSearchSuggestionsText] = React.useState(initalSearchText);
  const [hasFocus, setHasFocus] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const searchPageUrl = useDynamicData<NordicNest.Api.WellknownPagesApiModel>()[0]?.searchPageUrl || '';
  const elementRef = React.useRef<HTMLFormElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { close: closeMenu, menuState: menuStateOpen } = useDesktopMenuState();

  const { autoCompeleteSuggestions, searchSuggestions, historySuggestions } = useSearchSuggestions(
    searchText,
    SEARCH_HISTORY_COUNT,
    showSuggestions,
  );

  React.useImperativeHandle(
    ref,
    () => ({
      focusInput: () => {
        inputRef.current?.focus();
      },
    }),
    [],
  );

  // Combine all suggestions into a single array with `usedHistory` property
  const allSuggestions = React.useMemo(
    () =>
      [
        ...autoCompeleteSuggestions.map(s => ({ ...s, usedHistory: false })),
        ...searchSuggestions.map(s => ({ ...s, usedHistory: false })),
        ...historySuggestions.map(s => ({ ...s, usedHistory: true })),
      ] as AllSuggestionsType[],
    [autoCompeleteSuggestions, searchSuggestions, historySuggestions],
  );

  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);

  React.useEffect(
    () =>
      historyEvent.on(event => {
        if (event.event === HistoryEventType.Pop) {
          const currentUrlSearchQuery = new URLX(event.url).searchParams.get(SEARCH_QUERY_NAME) || '';
          setSearchText(currentUrlSearchQuery);
        } else if (event.event === HistoryEventType.Push && !hasFocus) {
          setSearchText('');
          setSearchSuggestionsText('');
        }
      }),
    [hasFocus],
  );

  React.useEffect(() => {
    isHardReload = false;
  }, []);

  const seachInputAreaClick = React.useCallback(() => {
    if (menuStateOpen) {
      closeMenu();
      // Wait for the menu to close before focusing the input
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
        setHasFocus(true);
      }, 100);
    }
  }, [closeMenu, menuStateOpen]);

  const close = React.useCallback(() => {
    if (hasDebounceSearched) {
      addSearchEventToDataLayer({
        originalSearch: searchText,
        searchPhrase: searchText,
        usedHistory: false,
        usedSuggestion: false,
      });
    }
    hasDebounceSearched = false;
    if (showSuggestions) {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      if (inputRef.current !== document.activeElement) {
        setTimeout(() => {
          setHasFocus(false);
        }, 200);
      }
    } else {
      setHasFocus(false);
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
    props.close?.();
  }, [showSuggestions, props, searchText]);

  const clearTimeouts = React.useCallback(() => {
    clearTimeout(previewTimeoutRef.current);
    clearTimeout(searchTimeoutRef.current);
    clearTimeout(suggestionsTimeoutRef.current);
  }, []);

  const search = React.useCallback(
    async (
      query: string,
      pushUrlState = true,
      usedSuggestion = false,
      usedHistory = false,
      sendEvent = false,
      originalQuery = '',
    ) => {
      clearTimeouts();
      if (query.length > 1) {
        const newUrl = searchUrl(
          searchPageUrl,
          query,
          pushUrlState,
          usedSuggestion,
          usedHistory,
          originalQuery,
          currentUrl().toString(),
        );
        const load = loadPage(newUrl, { shouldUseCache: false });
        load.on(page => {
          if (pushUrlState && isPage(page)) {
            pushState(new URLX(page.url), { page });
          }
          if (
            isComponentType<NordicNest.Api.SearchPageApiModel>(page) &&
            page.pagination.total > 0 &&
            page.originalSearch === page.searchPhrase
          ) {
            addToSearchHistory(query);
          }
          if (sendEvent) {
            addSearchEventToDataLayer({
              originalSearch: searchText,
              searchPhrase: query,
              usedHistory,
              usedSuggestion,
            });
          }
        });
      }
    },
    [clearTimeouts, searchPageUrl, searchText],
  );

  const applySuggestion = React.useCallback(
    async (phrase: string, usedHistory?: boolean) => {
      hasDebounceSearched = false;
      search(phrase, true, true, usedHistory, true, searchText);
      setSearchText(phrase);
      setSearchSuggestionsText(phrase);
      close();
    },
    [close, search, searchText],
  );

  const debounceSearchSuggestions = React.useCallback((query: string) => {
    clearTimeout(suggestionsTimeoutRef.current);

    suggestionsTimeoutRef.current = window.setTimeout(() => {
      setSearchSuggestionsText(query);
    }, DEBOUNCE_TIME);
  }, []);

  const debounceSearch = React.useCallback(
    (query: string) => {
      clearTimeouts();
      shouldPushUrlState.current = true;

      searchTimeoutRef.current = window.setTimeout(() => {
        search(query, shouldPushUrlState.current);
      }, DEBOUNCE_TIME * 8);

      previewTimeoutRef.current = window.setTimeout(() => {
        const url = searchUrl(searchPageUrl, query, false, false, false, query, currentUrl().toString());
        const load = loadPage(url);
        hasDebounceSearched = true;
        load.on(page => {
          if (isComponentType<NordicNest.Api.SearchPageApiModel>(page) && page.pagination.total > 0 && isPage(page)) {
            shouldPushUrlState.current = false;
            replaceState(new URLX(page.url), { page });
          } else if (!isComponentType<NordicNest.Api.SearchPageApiModel>(page) && isPage(page)) {
            shouldPushUrlState.current = false;
            pushState(new URLX(page.url), { page });
          }
        });
      }, DEBOUNCE_TIME);

      debounceSearchSuggestions(query);
    },
    [clearTimeouts, search, searchPageUrl, debounceSearchSuggestions],
  );

  const onSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (searchText.length > 0) {
        clearTimeouts();
        hasDebounceSearched = false;
        search(searchText, true, false, false, true, searchText);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        close();
      }
    },
    [clearTimeouts, close, search, searchText],
  );

  const queryFromValue = React.useCallback(
    (value: string) => {
      // IE11 and below fires an `input` on focus even if the user didn't type anything
      if (value === searchText) {
        return;
      }
      setSearchText(value);

      if (value.length > 0) {
        setShowSuggestions(true);
        setHighlightedIndex(-1);

        if (props.isLargeViewport) {
          debounceSearch(value);
        } else {
          debounceSearchSuggestions(value);
        }
      } else {
        clearTimeouts();
      }
    },
    [searchText, props.isLargeViewport, debounceSearch, debounceSearchSuggestions, clearTimeouts],
  );

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      queryFromValue(e.currentTarget.value);
    },
    [queryFromValue],
  );

  const onFocus = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (!menuStateOpen) {
        const value = e.currentTarget.value;
        e.currentTarget.selectionStart = value.length;
        setHasFocus(true);
        setSearchText(value);
        setShowSuggestions(true);
      }
    },
    [menuStateOpen],
  );

  //This is needed for search suggestions to close when tabbing away from it when it is opened.
  const closeSuggestions = React.useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Tab':
          if (document.activeElement instanceof Node && !elementRef?.current?.contains(document.activeElement)) {
            close();
          }
          break;
        case 'Escape':
          close();
          break;
      }
    },
    [close],
  );

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (!showSuggestions || allSuggestions.length === 0) return;

      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        setHighlightedIndex(prevIndex =>
          prevIndex === -1 ? allSuggestions.length - 1 : prevIndex === 0 ? allSuggestions.length - 1 : prevIndex - 1,
        );
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        setHighlightedIndex(prevIndex =>
          prevIndex === -1 ? 0 : prevIndex === allSuggestions.length - 1 ? 0 : prevIndex + 1,
        );
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (highlightedIndex >= 0 && highlightedIndex < allSuggestions.length) {
          const selectedSuggestion = allSuggestions[highlightedIndex];
          applySuggestion(selectedSuggestion.text, selectedSuggestion.usedHistory);
        }
      }
    },
    [showSuggestions, highlightedIndex, allSuggestions, applySuggestion],
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  React.useEffect(() => {
    if (showSuggestions) {
      document.addEventListener('keyup', closeSuggestions);
    } else {
      document.removeEventListener('keyup', closeSuggestions);
    }
    return () => document.removeEventListener('keyup', closeSuggestions);
  }, [closeSuggestions, showSuggestions]);

  const onClickCloseButton = React.useCallback(() => {
    clearTimeouts();
    if (searchText.length > 0) {
      inputRef.current?.focus();
      setSearchText('');
      setSearchSuggestionsText('');
    } else {
      close();
    }
  }, [clearTimeouts, close, searchText.length]);

  useOnClickOutside(elementRef as any, close);

  const searchButton = (
    <SearchButton
      aria-disabled={!props.isLargeViewport && !showQuickSearch}
      tabIndex={!props.isLargeViewport && !showQuickSearch ? -1 : 0}
      square
      as={Theme.Transparent}
      type="submit"
      disableRippleEffect
      onClick={() => inputRef.current?.focus()}
      aria-label={translate(t => t.header.search)}
      {...NnTestIdProps.QuickSearchBtn}
    >
      <Search />
    </SearchButton>
  );

  const closeButton = (
    <CloseButton
      type="button"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClickCloseButton();
      }}
      as={Theme.Transparent}
      disableRippleEffect
      square
      aria-disabled={!props.isLargeViewport ? !showQuickSearch : !hasFocus}
      tabIndex={!showQuickSearch && !props.isLargeViewport ? -1 : 0}
      aria-label={translate(t => t.ariaLabelCloseButton)}
      css={styled(hasFocus && closeIconFocusDecorator)}
    >
      <Close />
    </CloseButton>
  );

  const searchInput = (
    <SearchInput
      ref={inputRef}
      tabIndex={!props.isLargeViewport && !showQuickSearch ? -1 : 0}
      aria-label={translate(t => t.quickSearchPlaceholderText)}
      aria-disabled={!props.isLargeViewport && !showQuickSearch}
      name="q"
      role="combobox"
      type="search"
      autoComplete="off"
      aria-autocomplete="list"
      aria-activedescendant={getSuggestionId(highlightedIndex, searchSuggestionsText)}
      aria-expanded={showSuggestions && allSuggestions.length > 0}
      aria-controls={SUGGESTIONS_ID}
      aria-owns={SUGGESTIONS_ID}
      value={searchText}
      placeholder={translate(t => t.quickSearchPlaceholderText)}
      onChange={onChange}
      onFocus={onFocus}
      autoFocus={props.autoFocus}
      {...NnTestIdProps.QuickSearchInput}
    />
  );

  return (
    <Base
      action={searchPageUrl}
      onSubmit={onSubmit}
      onClick={seachInputAreaClick}
      role="search"
      ref={r => {
        if (r) {
          elementRef.current = r;
          if (props.elementRef) {
            props.elementRef.current = r;
          }
        }
      }}
    >
      <Field css={hasFocus && focusDecorator}>
        {searchButton}
        {searchInput}
        {closeButton}
      </Field>
      <PositionedSuggestions
        css={showSuggestions && allSuggestions.length > 0 && visibleSuggestionsDecorator}
        visible={
          (Boolean(props.suggestionsEnabled) || Boolean(props.isLargeViewport)) &&
          showSuggestions &&
          allSuggestions.length > 0
        }
        searchText={searchSuggestionsText}
        applySuggestion={applySuggestion}
        allSuggestions={allSuggestions}
        highlightedIndex={highlightedIndex}
        handleKeyDown={handleKeyDown}
      />
    </Base>
  );
});

export default React.memo(styled(QuickSearch));

const Base = styled.form(
  createMinLargeViewportDecorator({
    alignItems: 'center',
    width: 400,
  }),
  {
    display: 'flex',
    zIndex: 1,
    position: 'relative',
    width: '100%',
  },
  createTransitionDecorator({ property: ['background', 'box-shadow'] }),
);

const SearchButton = styled(Button, {
  flexShrink: 0,
  padding: { xy: 0 },
  marginLeft: t => t.sharedSpacing.xs,
});

const Search = styled(SearchIcon, {
  width: 16,
  height: 16,
  margin: { xy: t => t.sharedSpacing.xs },
});

const CloseButton = styled(Button, {
  visibility: 'hidden',
  flexShrink: 0,
  padding: { xy: 0 },
  marginRight: t => t.sharedSpacing.xs,
});

const Close = styled(CloseIcon, {
  width: 16,
  height: 16,
  margin: { xy: t => t.sharedSpacing.xs },
});

const closeIconFocusDecorator = styled({
  visibility: 'visible',
});

const Field = styled.div(
  createTransitionDecorator({ property: ['background-color'] }),
  createMaxSmallViewportDecorator({
    margin: {
      x: t => (isSvenssons ? 0 : t.sharedSpacing.s),
      bottom: t => (isSvenssons ? 0 : t.sharedSpacing.xs),
    },
    border: {
      bottom: { color: t => (isSvenssons ? t.quickSearch?.borderColor : 'transparent') },
    },
  }),
  {
    borderRadius: t => t.quickSearch?.borderRadius,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    zIndex: 1,
    height: 34,
    border: {
      xy: { width: 'thin', color: 'transparent', style: 'solid' },
    },
    backgroundColor: t => t.quickSearch?.backgroundColor,
  },
);

const SearchInput = styled.input({
  fontSize: t => (isIOS() ? t.sharedFontSize.s : t.sharedFontSize.xs),
  padding: { left: t => t.sharedSpacing.s },
  width: '100%',
  height: 'inherit',
  backgroundColor: 'transparent',
  border: { xy: { style: 'none' } },
  ':focus': {
    border: { xy: { style: 'none' } },
    outline: { style: 'none' },
  },
  flexGrow: 1,
  ['::-webkit-search-cancel-button' as any]: {
    display: 'none',
  },
});

const focusDecorator = styled(
  createMaxSmallViewportDecorator({
    border: {
      x: { color: isSvenssons ? 'transparent' : t => t.quickSearch?.borderColor },
      y: { color: t => t.quickSearch?.borderColor },
    },
  }),
  {
    backgroundColor: 'white',
    border: {
      xy: { color: t => t.quickSearch?.borderColor },
    },
  },
);

const PositionedSuggestions = styled(
  Suggestions,
  createTransitionDecorator({ property: ['border-color', 'box-shadow'] }),
  createMinLargeViewportDecorator({ width: '100%', left: 0 }),
  {
    borderRadius: t => t.searchSuggestions?.borderRadius,
    border: { xy: { color: 'transparent', style: 'solid', width: 0 } },
    position: 'absolute',
    width: t => (isSvenssons ? '100%' : `calc(100% - ${t.sharedSpacing.l}px)`),
    top: t => t.searchSuggestions?.position,
    left: t => (isSvenssons ? 0 : t.sharedSpacing.s),
  },
);

const visibleSuggestionsDecorator = styled({
  border: { xy: { color: t => t.searchSuggestions?.borderColor, style: 'solid' } },
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
});
